import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import React, { ReactNode, useEffect } from 'react'
import { ApolloProvider } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import { useTimeout } from 'usehooks-ts'

import FeatureFlagLink from 'main/components/common/FeatureFlagLink'
import GenericError from 'main/components/common/GenericError'
import Loader from 'main/components/common/Loader'
import { UserFieldsFragment, UserWithFamilyFieldsFragment } from 'main/gql/user'
import { SSOContextProvider } from 'main/hooks/useSSOContext'
import identifyOrReset from 'main/util/analytics/segment'
import { isInWhitelist } from 'main/util/error'
import { NetworkRating } from 'main/util/networks'
import { logError } from 'main/util/paymentMethod/common'
import { SendbirdContext, useSendbird } from 'main/util/sendbird'

import { IpLocationFieldsFragment } from '../gql/ipLocation'
import {
  AppQuery as IData,
  AppQuery_ipLocation as IpLocation,
  AppQuery_user as User,
} from './__generated__/AppQuery'
import client from './apolloClient'
import AppContext from './AppContext'
import { WhitelabelProvider } from './WhiteLabelContext'

const APP_QUERY = gql`
  query AppQuery {
    user {
      ...UserFields
      ...UserWithFamilyFields
      sendbirdAccessToken
    }
    currentNetwork {
      id
      name
      autoMigrateCcms
      whitelabelOptions
      availableLocales
      products
      origin
      ratings {
        id
        label
        importedValue
        position
        networkId
        slug
        symbolCount
      }
      subsidies {
        id
        name
        networkId
      }
      programOfferings {
        id
        name
        editable
        slug
      }
    }
    ipLocation {
      ...IpLocationFields
    }
  }

  ${UserFieldsFragment}
  ${UserWithFamilyFieldsFragment}
  ${IpLocationFieldsFragment}
`

interface IProps {
  children: ReactNode
}

const MIN_TIMEOUT_ERROR = 2000

export default (props: IProps) => {
  const { children } = props
  const { loading, error, data } = useQuery<IData>(APP_QUERY, { client })
  // use ref, not state, because we want to silently flip the lock if there is no error
  const showErrorRef = React.useRef(false)
  // use the state if we want to force a render if the error is set before the timeout
  const [_, setShowError] = React.useState(false)
  const forceErrorRender = () => setShowError(true)
  const { i18n } = useTranslation()

  const appUser: User | null = data && data.user ? data.user : null
  const preferredLanguage = data && data.user && data.user.preferredLanguage
  const location = data && data.ipLocation ? data.ipLocation : null

  const [user, setUser] = React.useState<User | null>(appUser)
  const [ipLocation, setIpLocation] = React.useState<IpLocation | null>(location)

  // after waiting a minimum time, unlock the errorRef and also force render if the error has already been set
  useTimeout(() => {
    showErrorRef.current = true
    if (error) {
      // if error is already set, we'll want to re-render so we force it
      forceErrorRender()
    }
  }, MIN_TIMEOUT_ERROR)

  useEffect(() => {
    if (preferredLanguage) {
      i18n.changeLanguage(preferredLanguage)
    }
  }, [preferredLanguage])

  useEffect(() => {
    setUser(appUser)
  }, [appUser])

  useEffect(() => {
    setIpLocation(location)
  }, [location])

  if (error || loading || !data) {
    const whitelist = ['Store reset while query was in flight (not completed in link chain)']

    if (error) {
      logError('error in app.tsx', { error, userAgent: window.navigator.userAgent })
    }
    //
    return error && !isInWhitelist(error.message, whitelist) && showErrorRef.current ? (
      <GenericError heading={'Sorry, something unexpected happened. (1)'} />
    ) : (
      <Loader />
    )
  }

  // @ts-ignore
  const appId = window.SENDBIRD_APP_ID
  const userId = user && user.id
  const accessToken = data!.user && data.user.sendbirdAccessToken
  identifyOrReset(user)

  const enabledProducts: string[] = data?.currentNetwork?.products || []
  const networkRatings: NetworkRating[] = data?.currentNetwork?.ratings || []
  const subsidiesForNetwork: any[] = data?.currentNetwork?.subsidies || []
  const networkProgramOfferings: any[] = data?.currentNetwork?.programOfferings || []
  const whitelabelOptions = JSON.parse(data?.currentNetwork?.whitelabelOptions || '{}')
  const networkName = data?.currentNetwork?.name
  const networkId = data?.currentNetwork?.id
  const networkAutoMigrateCcms = data?.currentNetwork?.autoMigrateCcms
  const networkOrigin = data?.currentNetwork?.origin
  const availableLocales = data?.currentNetwork?.availableLocales || ['en', 'es', 'ht', 'vi']

  return (
    <>
      <FeatureFlagLink />
      <ApolloProvider client={client}>
        <AppContext.Provider value={{ user, setUser, ipLocation, setIpLocation }}>
          <WhitelabelProvider
            enabledProducts={enabledProducts}
            networkName={networkName}
            networkId={networkId}
            networkAutoMigrateCcms={networkAutoMigrateCcms}
            networkProgramOfferings={networkProgramOfferings}
            availableLocales={availableLocales}
            whitelabelOptions={whitelabelOptions}
            networkRatings={networkRatings}
            subsidiesForNetwork={subsidiesForNetwork}
            networkOrigin={networkOrigin}
          >
            <SendbirdWrapper appId={appId} userId={userId} accessToken={accessToken}>
              <SSOContextProvider>{children}</SSOContextProvider>
            </SendbirdWrapper>
          </WhitelabelProvider>
        </AppContext.Provider>
      </ApolloProvider>
    </>
  )
}

interface IWrapperProps {
  appId: string
  userId: number | null
  accessToken: string | null
  children: ReactNode
}

function SendbirdWrapper(props: IWrapperProps) {
  const { appId, userId, accessToken, children } = props
  const sendBird = useSendbird(appId, userId, accessToken)

  return <SendbirdContext.Provider value={sendBird}>{children}</SendbirdContext.Provider>
}
